import { useCallback, useRef, useState } from 'react';
import {
  Box,
  CustomTheme,
  Drawer,
  Fab,
  IconButton,
  Stack,
  styled,
  SvgIcon,
  Tooltip,
  Typography,
  TooltipProps,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChatContainer from 'op-pages/RO/AiSummary/components/ChatPanel';
import { AutoAwesome } from '@mui/icons-material';
import { useActionPopoverContext } from 'op-contexts';
import FloatingPopoverContainer from 'op-components/RO/PatientFloatingActions/FloatingPopoverContainer';
import NewChatIcon from 'shared-components/images/newchat.svg?react';
import { logDatadogRumCustomAction } from 'op-utils/datadogRumCustomAction';

interface Props {
  patientId: string;
  positionKey?: string;
}

interface ChatPopoverTooltipProps extends TooltipProps {
  popperContainerEl?: HTMLElement | null;
}

const ChatPopoverTooltip = styled(({ className, popperContainerEl, ...props }: ChatPopoverTooltipProps) => (
  <Tooltip
    {...props}
    PopperProps={{
      disablePortal: Boolean(popperContainerEl),
      container: popperContainerEl ?? undefined,
    }}
    classes={{ popper: className }}
  />
))(() => ({}));

const ChatPopover = ({ positionKey, patientId }: Props): JSX.Element | null => {
  const theme = useTheme<CustomTheme>();
  const [isAskHistoryOpen, setIsAskHistoryOpen] = useState(false);
  const startNewConversationRef = useRef<(() => void) | null>(null);
  const {
    anchorEl,
    isAskPopoverOpen,
    openFloatingPanel,
    closeFloatingPanel,
    isFocusedModalOpen,
    popoverContainerEl,
    setPopoverContainerEl,
  } = useActionPopoverContext();

  const handlePopoverPaperRef = useCallback(
    (node: HTMLDivElement | null) => {
      setPopoverContainerEl((prev) => (prev === node ? prev : node));
    },
    [setPopoverContainerEl],
  );

  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [isChatScrolled, setIsChatScrolled] = useState(false);

  // Helper to reset both popover and history state
  const resetPopoverState = useCallback(() => {
    closeFloatingPanel();
    setIsAskHistoryOpen(false);
  }, [closeFloatingPanel]);

  const handleAskOpen = useCallback(() => {
    logDatadogRumCustomAction('ask_anywhere_open_fab', {
      patientId,
      viewMode: 'popover',
    });
    openFloatingPanel('ask');
    setIsAskHistoryOpen(false);
  }, [openFloatingPanel]);

  const handleAskClose = useCallback(() => {
    resetPopoverState();
  }, [resetPopoverState]);

  const handleOpenAskPage = () => {
    if (!patientId) return;

    logDatadogRumCustomAction('ask_anywhere_open_ask_page', {
      patientId,
      viewMode: 'popover',
    });
    window.open(`/radiation/patient/${patientId}/chat`, '_blank', 'noopener,noreferrer');
    handleAskClose();
  };

  const isNewConversationActive = selectedConversationId === null;

  const FabSection = (): JSX.Element => (
    <Fab
      color="primary"
      aria-label="Open Ask Anywhere"
      data-testid="ask-anywhere-fab"
      tabIndex={0}
      onClick={handleAskOpen}
      sx={{
        zIndex: isFocusedModalOpen ? theme.zIndex.modal - 2 : theme.zIndex.fab,
        background:
          'conic-gradient(from 59deg at 50% 50%, #3EA7B2 63.080899715423584deg, #AAA1FD 162.69230604171753deg, #FC8891 233.65383625030518deg, #AAA1FD 327.11538791656494deg)',
        transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out, filter 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 8,
          filter: 'brightness(1) saturate(1.3)',
          transform: 'translateY(-1px) scale(1.1)',
        },
      }}>
      <AutoAwesome sx={{ color: theme.palette.common.white }} />
    </Fab>
  );

  const HeaderSection = (): JSX.Element => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <ChatPopoverTooltip title="Conversation history" popperContainerEl={popoverContainerEl}>
          <IconButton
            aria-label={isAskHistoryOpen ? 'Hide history' : 'Show history'}
            tabIndex={0}
            data-testid="ask-anywhere-history-toggle"
            onClick={() => {
              if (!isAskHistoryOpen) {
                logDatadogRumCustomAction('ask_anywhere_history_open', {
                  patientId,
                  viewMode: 'popover',
                });
              }
              setIsAskHistoryOpen((prev) => !prev);
            }}>
            <MenuIcon fontSize="medium" />
          </IconButton>
        </ChatPopoverTooltip>
        <Typography variant="h6">Ask</Typography>
      </Stack>

      <Stack direction="row" alignItems="center" gap={1}>
        {!isNewConversationActive && (
          <ChatPopoverTooltip title="New conversation" popperContainerEl={popoverContainerEl}>
            <IconButton
              tabIndex={0}
              aria-label="New conversation"
              color="primary"
              data-testid="ask-anywhere-new-conversation"
              onClick={() => {
                logDatadogRumCustomAction('ask_anywhere_new_conversation', {
                  patientId,
                  viewMode: 'popover',
                });
                startNewConversationRef.current?.();
                setIsAskHistoryOpen(false);
              }}>
              <SvgIcon color="primary" component={NewChatIcon} sx={{ fontSize: '22px' }} />
            </IconButton>
          </ChatPopoverTooltip>
        )}
        <ChatPopoverTooltip title="Full screen" popperContainerEl={popoverContainerEl}>
          <IconButton aria-label="Full screen" data-testid="ask-anywhere-full-screen" onClick={handleOpenAskPage}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </ChatPopoverTooltip>
        <ChatPopoverTooltip title="Close" popperContainerEl={popoverContainerEl}>
          <IconButton aria-label="Close Ask Anywhere" data-testid="ask-anywhere-close" onClick={handleAskClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </ChatPopoverTooltip>
      </Stack>
    </Box>
  );

  return (
    <FloatingPopoverContainer
      fab={<FabSection />}
      isOpen={isAskPopoverOpen}
      anchorEl={anchorEl}
      onClose={handleAskClose}
      isFocusedModalOpen={isFocusedModalOpen}
      positionKey={positionKey}
      onPositionKeyChange={resetPopoverState}
      onPopoverPaperRef={handlePopoverPaperRef}
      header={<HeaderSection />}
      isHeaderScrolled={isChatScrolled}>
      <Box sx={{ width: '100%', height: '100%' }}>
        <ChatContainer
          patientId={patientId}
          isPopoverLayout
          onSelectedConversationIdChange={setSelectedConversationId}
          onConversationSelected={() => setIsAskHistoryOpen(false)}
          onScrollStateChange={setIsChatScrolled}
          startNewConversationRef={startNewConversationRef}
          renderHistoryWrapper={(list) => (
            <Drawer
              anchor="left"
              open={isAskHistoryOpen}
              onClose={() => setIsAskHistoryOpen(false)}
              variant="temporary"
              transitionDuration={400}
              disablePortal={Boolean(popoverContainerEl)}
              container={popoverContainerEl ?? undefined}
              ModalProps={{
                sx: {
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                },
              }}
              slotProps={{
                backdrop: {
                  sx: {
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                  },
                },
              }}
              PaperProps={{
                zIndex: -7,
                sx: {
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: 320,
                  maxWidth: '100%',
                },
              }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" py={2} px={3} mt={2}>
                <Typography variant="h6">Conversations</Typography>
                <IconButton
                  tabIndex={0}
                  aria-label="Close conversation list"
                  data-testid="ask-history-close"
                  onClick={() => setIsAskHistoryOpen(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
              {list}
            </Drawer>
          )}
        />
      </Box>
    </FloatingPopoverContainer>
  );
};

export default ChatPopover;
